import type { PopoverProps } from '@byecode/ui'
import { AutoResizeDropdown, IconFont, Popover, usePopoverHeight } from '@byecode/ui'
import { Strategy } from '@floating-ui/react'
import { AppUserStatus } from '@lighthouse/core'
import { find, reduce } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'

import { CURRENT_USER, PERSON_ID, PERSON_MAP_NAME } from '../../constants'
import { useApplicationContext } from '../../contexts'
import type { RoleOption, UserOption } from '../../types'
import { popoverMaxHeight } from '../FieldInput/constant'
import { DropDownList, type PersonItemExtra } from './DropDownList'
import * as SC from './styles'
import { TagItemPreviewer } from './TagItemPreviewer'

// type OptionItem = { label: string; value: string; color?: string; isDelete?: boolean }
type PersonSelectStyles = 'rightSection' | 'container' | 'value' | 'dropContainer'

export { TagItemPreviewer } from './TagItemPreviewer'

interface PersonSelectProps extends Pick<PopoverProps, 'position'> {
    options: UserOption[]
    value?: string[]
    defaultValue?: string[]
    width?: string
    placeholder?: string
    rightProperty?: 'roleName' | 'userId'
    withinPortal?: boolean
    strategy?: Strategy
    color?: string
    clearable?: boolean
    tagClearable?: boolean
    style?: React.CSSProperties
    enableCheckBox?: boolean
    enableCurrentUser?: boolean
    searchPlaceholder?: string
    readOnly?: boolean
    isHiddenUserId?: boolean
    isMultiple?: boolean
    isBorder?: boolean
    isHoverShowIcon?: boolean
    isWrap?: boolean
    focusOutLine?: string
    dropDownWidth?: string
    minWidth?: number
    emptyDesc?: string
    valueComponent?: React.ReactNode
    extraComponent?: React.FC<{ onClose?: () => void }>
    styles?: Partial<Record<PersonSelectStyles, React.CSSProperties>>
    disabled?: boolean
    roleOptions?: RoleOption[]
    getItemExtra?: (user: UserOption) => PersonItemExtra
    placeholderColor?: string
    pageTarget?: string
    onVisibleChange?: (open: boolean) => void
    onChange?: (value: string[]) => void
}

export const PersonSelect: React.FunctionComponent<PersonSelectProps> = ({
    options,
    value,
    defaultValue = [],
    width,
    placeholder,
    withinPortal,
    strategy,
    color,
    isMultiple = true,
    clearable = true,
    tagClearable,
    isBorder = true,
    isHiddenUserId = false,
    readOnly,
    position = 'bottom-start',
    searchPlaceholder = '搜索',
    emptyDesc = '没有数据',
    enableCheckBox = true,
    isHoverShowIcon = false,
    rightProperty,
    enableCurrentUser,
    isWrap,
    focusOutLine,
    styles,
    valueComponent,
    extraComponent,
    minWidth,
    disabled,
    roleOptions,
    getItemExtra,
    placeholderColor,
    pageTarget,
    onVisibleChange,
    onChange
}) => {
    const [opened, setOpened] = useState(false)
    const contentRef = useRef<HTMLButtonElement>(null)

    const dropDownRef = useRef<HTMLDivElement>(null)

    const [currentValue, setCurrentValue] = useState(value?.filter(Boolean) ?? defaultValue)
    const [searchWord, setSearchWord] = useState('')

    const currentOptions = useMemo(
        () =>
            options.filter(
                item =>
                    (item.name.toLocaleLowerCase().includes(searchWord.toLocaleLowerCase()) ||
                        item.id.toLocaleLowerCase().includes(searchWord.toLocaleLowerCase())) &&
                    item.status !== AppUserStatus.DEPART
            ),
        [options, searchWord]
    )

    const { height: tableHeight, ref } = usePopoverHeight({ opened, popoverMaxHeight })

    const isEmptyValue = useMemo(() => currentValue.length === 0, [currentValue.length])

    const isShowRightSection = useMemo(() => {
        if (clearable) {
            return isEmptyValue
        }
        return !readOnly && !isHoverShowIcon
    }, [clearable, isEmptyValue, isHoverShowIcon, readOnly])

    const isShowClearIcon = useMemo(() => {
        return clearable && !isEmptyValue
    }, [clearable, isEmptyValue])

    const currentTags = useMemo(() => {
        return reduce<string, UserOption[]>(
            (preVal, userId) => {
                if (userId === '{currentUserId}') {
                    return [...preVal, CURRENT_USER]
                }
                if (PERSON_ID.has(userId)) {
                    return [...preVal, { id: userId, name: PERSON_MAP_NAME[userId] }]
                }
                const item = find(item => item.id === userId, options)
                if (!item) {
                    return preVal
                }
                if (item.status === AppUserStatus.DEPART) {
                    return [...preVal, { ...item, name: `${item.name}(已注销)` }]
                }
                return [...preVal, item]
            },
            [],
            currentValue
        )
    }, [currentValue, options])

    useUpdateEffect(() => {
        if (currentValue !== value) {
            setCurrentValue(value ?? defaultValue)
        }
    }, [value])

    const handleClear = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
            event.stopPropagation()
            setCurrentValue([])
            onChange?.([])
        },
        [onChange]
    )

    const handleOpen = useCallback(() => {
        if (readOnly) {
            return
        }
        setOpened(draft => !draft)
        onVisibleChange?.(!opened)
    }, [onVisibleChange, opened, readOnly])

    const handleClose = useCallback(() => {
        setOpened(false)
        onVisibleChange?.(false)
    }, [onVisibleChange])

    const handMultipleChange = useCallback(
        (id: string) => {
            const isExit = find(userId => userId === id, currentValue)
            if (isMultiple) {
                const filterValue = currentValue.filter(userId => userId !== id)
                const newValue = isExit ? filterValue : [...filterValue, id]
                setCurrentValue(newValue)
                onChange?.(newValue)
                return
            }
            const newValue = isExit ? [] : [id]
            setCurrentValue(newValue)
            onChange?.(newValue)
            handleClose()
        },
        [currentValue, handleClose, isMultiple, onChange]
    )

    const handleRemove = useCallback(
        (id: string) => {
            handMultipleChange(id)
        },
        [handMultipleChange]
    )

    return (
        <Popover
            target={pageTarget ? document.querySelector<HTMLElement>(pageTarget) : undefined}
            strategy={strategy}
            opened={opened}
            withinPortal={withinPortal}
            offsetOptions={{ mainAxis: 16 }}
            position={position}
            onClose={handleClose}
            closeOnEscape
            disabled={disabled}
            minWidth={minWidth}
            width="auto"
            // positionDependencies={[currentOptions]}
        >
            <Popover.Target>
                <SC.PersonContainer
                    ref={contentRef}
                    opened={opened}
                    width={width}
                    isBorder={isBorder}
                    focusOutLine={focusOutLine}
                    style={styles?.container}
                    type="button"
                >
                    <SC.ValueContainer onClick={handleOpen} style={styles?.value} ref={ref}>
                        {valueComponent ?? (
                            <>
                                <SC.TagWrapper isWrap={isWrap}>
                                    {currentTags.map(item => (
                                        <TagItemPreviewer
                                            key={item.id}
                                            data={item}
                                            disabled={disabled}
                                            isHiddenUserId={isHiddenUserId}
                                            clearable={!readOnly && tagClearable}
                                            onRemove={handleRemove}
                                        />
                                    ))}
                                    {isEmptyValue && <SC.PlaceHolder color={placeholderColor}>{placeholder}</SC.PlaceHolder>}
                                </SC.TagWrapper>
                                <SC.IconWrapper data-field-icon style={styles?.rightSection}>
                                    {isShowRightSection && (
                                        <SC.ArrowIcon
                                            type="ArrowDownSmall"
                                            size={16}
                                            // color="var(--color-gray-400)"
                                        />
                                    )}
                                    {isShowClearIcon && (
                                        <SC.ClearIcon
                                            size={16}
                                            // color="var(--color-gray-400)"
                                            onClick={handleClear}
                                            type="CloseCircle"
                                        />
                                    )}
                                </SC.IconWrapper>
                            </>
                        )}
                    </SC.ValueContainer>
                </SC.PersonContainer>
            </Popover.Target>
            <AutoResizeDropdown compact offset={48}>
                <SC.DropDownContainer
                    data-type="fl-node-part"
                    width={width}
                    ref={dropDownRef}
                    popoverHeight={tableHeight}
                    style={styles?.dropContainer}
                >
                    <SC.SearchInput
                        value={searchWord}
                        autoFocus
                        onChange={e => setSearchWord(e.target.value)}
                        onBlur={e => setSearchWord(e.target.value)}
                        placeholder={searchPlaceholder}
                        borderColor={focusOutLine}
                        prefix={<IconFont type="Search" size={16} color="var(--color-gray-500)" />}
                    />
                    <SC.DropDownContent>
                        <DropDownList
                            options={currentOptions}
                            color={color}
                            roleOptions={roleOptions}
                            getItemExtra={getItemExtra}
                            rightProperty={rightProperty}
                            value={currentValue}
                            enableCheckBox={enableCheckBox}
                            searchWord={searchWord}
                            emptyDesc={emptyDesc}
                            onMultipleChange={handMultipleChange}
                            onClose={handleClose}
                            extraComponent={extraComponent}
                            enableCurrentUser={enableCurrentUser}
                        />
                    </SC.DropDownContent>
                </SC.DropDownContainer>
            </AutoResizeDropdown>
        </Popover>
    )
}
